import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useAuth,
  KBCategory,
  KnowledgeBaseArticle,
  KnowledgeBaseSection,
  driverNeedsKnowledgeTraining,
  topicsInCategory,
} from '../context/AuthContext';
import {
  KNOWLEDGE_QUIZ_PASS_PERCENT,
  isKnowledgeQuizLocked,
} from '../knowledgeQuiz';

const getCategoryIcon = (color: string) => {
  if (color === '#1565C0') {
    return <LocalShippingIcon sx={{ color: 'white' }} />;
  }
  if (color === '#2E7D32') {
    return <PhoneIphoneIcon sx={{ color: 'white' }} />;
  }
  return <HelpOutlineIcon sx={{ color: 'white' }} />;
};

export default function DriverTraining() {
  const {
    currentUser,
    knowledgeBase,
    saveKnowledgeArticle,
    deleteKnowledgeArticle,
    reorderKnowledgeTopics,
    kbSections,
    updateKnowledgeSection,
    markKnowledgeTopicRead,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const needsTraining = driverNeedsKnowledgeTraining(currentUser);
  const [quizLocked, setQuizLocked] = useState(() => isKnowledgeQuizLocked(currentUser?.id));
  const [showQuizVoided] = useState(
    () =>
      Boolean((location.state as { quizVoided?: boolean } | null)?.quizVoided) ||
      sessionStorage.getItem('sds_quiz_voided') === '1'
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');

  // Admin Add/Edit Article Modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [formCategory, setFormCategory] = useState<KBCategory>('Delivery & Routing Procedures');
  const [formTitle, setFormTitle] = useState('');
  const [formSummary, setFormSummary] = useState('');
  const [formStepsText, setFormStepsText] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [savingArticle, setSavingArticle] = useState(false);
  const [sectionEditorKey, setSectionEditorKey] = useState<string | null>(null);
  const [sectionTitle, setSectionTitle] = useState('');
  const [sectionCategory, setSectionCategory] = useState('');
  const [sectionSubtitle, setSectionSubtitle] = useState('');
  const [savingSection, setSavingSection] = useState(false);
  const [kbFeedback, setKbFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );
  const [draggingTopicId, setDraggingTopicId] = useState<string | null>(null);
  const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);
  const [topicOrderPreview, setTopicOrderPreview] = useState<{
    category: KBCategory;
    ids: string[];
  } | null>(null);
  const dragRef = useRef<{ id: string; category: KBCategory; origin: string[] } | null>(null);
  const previewRef = useRef<string[] | null>(null);
  const finishingDragRef = useRef(false);

  useEffect(() => {
    const sync = () => setQuizLocked(isKnowledgeQuizLocked(currentUser?.id));
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, [currentUser?.id]);

  useEffect(() => {
    sessionStorage.removeItem('sds_quiz_voided');
  }, []);

  const openedInitialTopicRef = useRef(false);

  useEffect(() => {
    if (!needsTraining || openedInitialTopicRef.current) return;
    const orderedIds = kbSections.flatMap((section) =>
      topicsInCategory(knowledgeBase, section.key).map((article) => article.id)
    );
    if (orderedIds.length === 0) return;
    openedInitialTopicRef.current = true;
    const read = new Set(currentUser?.knowledgeTopicsRead || []);
    setExpandedTopicId(orderedIds.find((id) => !read.has(id)) ?? orderedIds[0]);
  }, [currentUser?.knowledgeTopicsRead, kbSections, knowledgeBase, needsTraining]);

  if (!currentUser) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '220px',
          bgcolor: 'grey.50',
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <LockOutlinedIcon color="primary" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              SDS Driver Sign-In Required
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Please log in with your Success Delivery Services credentials to access the Driver Knowledge Base.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (quizLocked) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '220px',
          bgcolor: 'grey.50',
          px: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Knowledge base locked
            </Typography>
            <Typography variant="body1" color="text.secondary">
              A quiz is open. This page stays locked so the guides cannot be used on another screen.
              Go back to the quiz window. Leaving that window ends the attempt.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  const isAdmin = currentUser.role === 'admin';
  const readIds = new Set(currentUser.knowledgeTopicsRead || []);
  const topicsRead = knowledgeBase.filter((article) => readIds.has(article.id)).length;
  const allTopicsRead = knowledgeBase.length > 0 && topicsRead === knowledgeBase.length;

  const openSectionEditor = (section: KnowledgeBaseSection) => {
    setSectionEditorKey(section.key);
    setSectionTitle(section.title);
    setSectionCategory(section.key);
    setSectionSubtitle(section.subtitle);
  };

  const handleSaveSection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sectionEditorKey) return;
    setSavingSection(true);
    const res = await updateKnowledgeSection(sectionEditorKey, {
      key: sectionCategory,
      title: sectionTitle,
      subtitle: sectionSubtitle,
    });
    setSavingSection(false);
    if (!res.success) {
      setKbFeedback({ type: 'error', text: res.error || 'Could not update the section.' });
      return;
    }
    if (selectedCategory === sectionEditorKey) {
      setSelectedCategory(sectionCategory.trim());
    }
    setKbFeedback({ type: 'success', text: `Updated section "${sectionTitle.trim()}".` });
    setSectionEditorKey(null);
  };

  const openNewArticleModal = (defaultCat: KBCategory = 'Delivery & Routing Procedures') => {
    setEditingId(undefined);
    setFormCategory(defaultCat);
    setFormTitle('');
    setFormSummary('');
    setFormStepsText('');
    setFormNotes('');
    setEditorOpen(true);
  };

  const openEditArticleModal = (article: KnowledgeBaseArticle) => {
    setEditingId(article.id);
    setFormCategory(article.category);
    setFormTitle(article.title);
    setFormSummary(article.summary);
    setFormStepsText(article.steps.join('\n'));
    setFormNotes(article.notes || '');
    setEditorOpen(true);
  };

  const handleSaveArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim() || !formSummary.trim()) return;

    const stepsArray = formStepsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    setSavingArticle(true);
    const res = await saveKnowledgeArticle({
      id: editingId,
      category: formCategory,
      title: formTitle,
      summary: formSummary,
      steps: stepsArray,
      notes: formNotes,
    });
    setSavingArticle(false);

    if (!res.success) {
      setKbFeedback({ type: 'error', text: res.error || 'Could not save Knowledge Base guide.' });
    } else {
      setKbFeedback({
        type: 'success',
        text: editingId
          ? `Updated guide "${formTitle.trim()}" in Firebase.`
          : `Added new guide "${formTitle.trim()}" to Firebase.`,
      });
      setEditorOpen(false);
    }
  };

  const clearTopicDrag = () => {
    dragRef.current = null;
    previewRef.current = null;
    setDraggingTopicId(null);
    setTopicOrderPreview(null);
  };

  const moveDraggedTopicTo = (clientY: number) => {
    const drag = dragRef.current;
    if (!drag) return;
    const order = previewRef.current ?? drag.origin;
    const rows = [...document.querySelectorAll<HTMLElement>('[data-topic-id]')].filter(
      (row) => row.dataset.topicCategory === drag.category
    );
    const over = rows.find((row) => {
      const rect = row.getBoundingClientRect();
      return clientY >= rect.top && clientY <= rect.bottom;
    });
    const overId = over?.dataset.topicId;
    if (!overId || overId === drag.id) return;

    const from = order.indexOf(drag.id);
    const to = order.indexOf(overId);
    if (from < 0 || to < 0 || from === to) return;

    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, drag.id);
    previewRef.current = next;
    setTopicOrderPreview({ category: drag.category, ids: next });
  };

  const finishTopicDrag = async () => {
    if (finishingDragRef.current) return;
    const drag = dragRef.current;
    const next = previewRef.current;
    if (!drag) return;
    finishingDragRef.current = true;
    const changed = !!next && next.join('|') !== drag.origin.join('|');
    const category = drag.category;
    const orderedIds = next ? [...next] : [];
    clearTopicDrag();
    if (!changed) {
      finishingDragRef.current = false;
      return;
    }
    const res = await reorderKnowledgeTopics(category, orderedIds);
    finishingDragRef.current = false;
    if (!res.success) {
      setKbFeedback({ type: 'error', text: res.error || 'Could not save the new topic order.' });
    }
  };

  const handleTopicDragStart = (
    event: React.PointerEvent<HTMLElement>,
    article: KnowledgeBaseArticle
  ) => {
    event.stopPropagation();
    event.preventDefault();
    const origin = topicsInCategory(knowledgeBase, article.category).map((item) => item.id);
    dragRef.current = { id: article.id, category: article.category, origin };
    previewRef.current = origin;
    setDraggingTopicId(article.id);
    setTopicOrderPreview({ category: article.category, ids: origin });

    const onMove = (moveEvent: PointerEvent) => {
      if (moveEvent.buttons === 0) {
        onUp();
        return;
      }
      moveDraggedTopicTo(moveEvent.clientY);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      void finishTopicDrag();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const handleDeleteArticle = async (article: KnowledgeBaseArticle) => {
    const res = await deleteKnowledgeArticle(article.id);
    if (res.success) {
      setKbFeedback({
        type: 'success',
        text: `Removed guide "${article.title}".`,
      });
    } else {
      setKbFeedback({
        type: 'error',
        text: res.error || 'Failed to remove guide.',
      });
    }
  };

  const filteredArticles = knowledgeBase.filter((article) => {
    const matchesCategory =
      selectedCategory === 'ALL' || article.category === selectedCategory;
    if (!matchesCategory) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      article.title.toLowerCase().includes(q) ||
      article.summary.toLowerCase().includes(q) ||
      article.category.toLowerCase().includes(q) ||
      article.steps.some((step) => step.toLowerCase().includes(q)) ||
      (article.notes && article.notes.toLowerCase().includes(q))
    );
  });

  const visibleTopicIds = kbSections.flatMap((section) => {
    const orderedArticles = topicsInCategory(filteredArticles, section.key);
    const catArticles =
      topicOrderPreview?.category === section.key
        ? topicOrderPreview.ids
            .map((id) => orderedArticles.find((article) => article.id === id))
            .filter((article): article is KnowledgeBaseArticle => Boolean(article))
        : orderedArticles;
    if (
      catArticles.length === 0 &&
      (!isAdmin || searchQuery.trim() || selectedCategory !== 'ALL')
    ) {
      return [];
    }
    return catArticles.map((article) => article.id);
  });

  const handleMarkTopicRead = async (articleId: string) => {
    const position = visibleTopicIds.indexOf(articleId);
    const nextId = position >= 0 ? visibleTopicIds[position + 1] ?? null : null;
    setExpandedTopicId(nextId);
    const res = await markKnowledgeTopicRead(articleId);
    if (!res.success) {
      setExpandedTopicId(articleId);
      setKbFeedback({
        type: 'error',
        text: res.error || 'Could not save your reading progress.',
      });
      return;
    }
    if (nextId) {
      window.setTimeout(() => {
        document.getElementById(`knowledge-topic-${nextId}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 60);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        paddingTop: { xs: '84px', sm: '260px', md: '230px' },
        pb: 8,
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          width: '100%',
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            mb: 2,
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0, textAlign: { xs: 'center', md: 'left' }, width: { xs: '100%', md: 'auto' } }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.75rem', sm: '2.125rem' },
                lineHeight: 1.2,
                background: 'linear-gradient(45deg, #0D47A1 30%, #1976d2 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Knowledge Base
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, mx: { xs: 'auto', md: 0 }, maxWidth: { xs: 360, md: 560 } }}
            >
              Step-by-step delivery procedures and EC-Mobile instructions.
            </Typography>
          </Box>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => openNewArticleModal()}
              sx={{
                fontWeight: 700,
                width: { xs: '100%', md: 'auto' },
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Add Knowledge Base Guide
            </Button>
          )}
        </Box>

        {needsTraining && (
          <Paper sx={{ p: { xs: 2, sm: 2.5 }, mb: 2, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              New driver training
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Read every topic and mark it as read. Then take the quiz. You need{' '}
              {KNOWLEDGE_QUIZ_PASS_PERCENT}% to pass. If you do not pass, review the guides and try
              again. Your schedule stays locked until you pass.
            </Typography>
            {showQuizVoided && (
              <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
                That attempt was closed because you left the quiz screen. Start the quiz again when
                you are ready to stay on that page.
              </Alert>
            )}
            {currentUser.knowledgeQuizScore != null && !currentUser.knowledgeQuizPassed && (
              <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
                Last score: {currentUser.knowledgeQuizScore}%. A passing grade is{' '}
                {KNOWLEDGE_QUIZ_PASS_PERCENT}%.
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Typography fontWeight={700}>
                {topicsRead} of {knowledgeBase.length} topics read
              </Typography>
              <Button
                variant="contained"
                disabled={!allTopicsRead}
                onClick={() => navigate('/quiz')}
              >
                Start quiz
              </Button>
            </Box>
          </Paper>
        )}

        {kbFeedback && (
          <Alert
            severity={kbFeedback.type}
            onClose={() => setKbFeedback(null)}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {kbFeedback.text}
          </Alert>
        )}

        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                order: { xs: 2, md: 1 },
                flex: 1,
                minWidth: 0,
              }}
            >
              <Chip
                label="All Topics"
                color={selectedCategory === 'ALL' ? 'primary' : 'default'}
                variant={selectedCategory === 'ALL' ? 'filled' : 'outlined'}
                onClick={() => setSelectedCategory('ALL')}
                sx={{ fontWeight: 600, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 } }}
              />
              {kbSections.map((cat) => (
                <Chip
                  key={cat.key}
                  label={cat.key}
                  color={selectedCategory === cat.key ? 'primary' : 'default'}
                  variant={selectedCategory === cat.key ? 'filled' : 'outlined'}
                  onClick={() => setSelectedCategory(cat.key)}
                  sx={{ fontWeight: 600, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 } }}
                />
              ))}
            </Box>

            <TextField
              size="small"
              placeholder="Search procedures, EC-Mobile, scanning..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                order: { xs: 1, md: 2 },
                width: { xs: '100%', md: 360 },
                flexShrink: 0,
                bgcolor: 'white',
                borderRadius: 1,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Grid container spacing={3}>
            {kbSections.map((cat) => {
              const orderedArticles = topicsInCategory(filteredArticles, cat.key);
              const catArticles =
                topicOrderPreview?.category === cat.key
                  ? topicOrderPreview.ids
                      .map((id) => orderedArticles.find((article) => article.id === id))
                      .filter((article): article is KnowledgeBaseArticle => Boolean(article))
                  : orderedArticles;
              if (
                catArticles.length === 0 &&
                (!isAdmin || searchQuery.trim() || selectedCategory !== 'ALL')
              ) {
                return null;
              }

              return (
                <Grid item xs={12} key={cat.key}>
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {/* Section Header */}
                    <Box
                      sx={{
                        px: { xs: 2, sm: 3 },
                        py: 2,
                        bgcolor: cat.color,
                        color: 'white',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.18)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {getCategoryIcon(cat.color)}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ lineHeight: 1.25, fontSize: { xs: '1rem', sm: '1.25rem' }, overflowWrap: 'anywhere' }}
                          >
                            {cat.title}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', overflowWrap: 'anywhere' }}>
                            {cat.subtitle}
                          </Typography>
                        </Box>
                      </Box>

                      {isAdmin && (
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => openSectionEditor(cat)}
                            sx={{
                              color: 'white',
                              borderColor: 'rgba(255,255,255,0.5)',
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                borderColor: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)',
                              },
                            }}
                          >
                            Edit Section
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => openNewArticleModal(cat.key)}
                            sx={{
                              color: 'white',
                              borderColor: 'rgba(255,255,255,0.5)',
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                borderColor: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)',
                              },
                            }}
                          >
                            Add Topic
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {/* Expandable Articles Accordion List */}
                    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, bgcolor: 'background.paper' }}>
                      {catArticles.map((article, index) => (
                        <Box
                          key={article.id}
                          id={`knowledge-topic-${article.id}`}
                          data-topic-id={article.id}
                          data-topic-category={cat.key}
                          sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 0.5,
                            mb: index < catArticles.length - 1 ? 1.5 : 0,
                            borderRadius: '10px',
                            scrollMarginTop: { xs: '100px', sm: '280px' },
                            opacity: draggingTopicId === article.id ? 0.55 : 1,
                          }}
                        >
                          {isAdmin && !searchQuery.trim() && (
                            <Box
                              role="button"
                              aria-label={`Drag to reorder ${article.title}`}
                              title="Drag to reorder"
                              onPointerDown={(event) => handleTopicDragStart(event, article)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                flexShrink: 0,
                                cursor: 'grab',
                                color: 'text.secondary',
                                touchAction: 'none',
                                borderRadius: 1,
                                '&:active': { cursor: 'grabbing' },
                              }}
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </Box>
                          )}
                        <Accordion
                          {...(needsTraining
                            ? {
                                expanded: expandedTopicId === article.id,
                                onChange: (_event: React.SyntheticEvent, isExpanded: boolean) => {
                                  setExpandedTopicId(isExpanded ? article.id : null);
                                },
                              }
                            : { defaultExpanded: index === 0 })}
                          disableGutters
                          elevation={0}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            border: '1px solid',
                            borderColor:
                              draggingTopicId === article.id ? 'primary.main' : 'divider',
                            borderRadius: '10px !important',
                            '&:before': { display: 'none' },
                            overflow: 'hidden',
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              bgcolor: 'grey.50',
                              px: { xs: 1.5, sm: 2.5 },
                              py: 0.5,
                              '&:hover': { bgcolor: 'grey.100' },
                              '& .MuiAccordionSummary-content': { minWidth: 0, my: 1 },
                            }}
                          >
                            <Box
                              sx={{
                                width: '100%',
                                minWidth: 0,
                                pr: 0.5,
                              }}
                            >
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                }}
                              >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={700}
                                  color="text.primary"
                                  sx={{ lineHeight: 1.3, overflowWrap: 'anywhere' }}
                                >
                                  {article.title}
                                </Typography>
                              </Box>

                              {needsTraining && readIds.has(article.id) && (
                                <Chip size="small" color="success" label="Read" sx={{ flexShrink: 0 }} />
                              )}
                              {isAdmin && (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    title="Edit guide"
                                    onClick={() => openEditArticleModal(article)}
                                  >
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    title="Delete guide"
                                    onClick={() => handleDeleteArticle(article)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.25, overflowWrap: 'anywhere' }}
                              >
                                {article.summary}
                              </Typography>
                              </Box>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails sx={{ px: { xs: 1.5, sm: 3 }, py: 2.5 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              color="text.secondary"
                              sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem' }}
                            >
                              Step-by-Step Procedure
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                              {article.steps.map((step, sIdx) => (
                                <Box
                                  key={sIdx}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                  }}
                                >
                                  <CheckCircleOutlineIcon
                                    sx={{ color: cat.color, fontSize: 20, mt: 0.25, flexShrink: 0 }}
                                  />
                                  <Typography variant="body2" sx={{ lineHeight: 1.6, minWidth: 0, overflowWrap: 'anywhere' }}>
                                    <strong>Step {sIdx + 1}:</strong> {step}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>

                            {needsTraining && !readIds.has(article.id) && (
                              <Button
                                variant="contained"
                                sx={{ mt: 2.5 }}
                                onClick={() => handleMarkTopicRead(article.id)}
                              >
                                I have read this topic
                              </Button>
                            )}

                            {article.notes && (
                              <Paper
                                variant="outlined"
                                sx={{
                                  mt: 2.5,
                                  p: 1.5,
                                  borderRadius: 2,
                                  bgcolor: 'rgba(25, 118, 210, 0.05)',
                                  borderColor: 'rgba(25, 118, 210, 0.25)',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                }}
                              >
                                <InfoOutlinedIcon color="primary" fontSize="small" sx={{ mt: 0.2 }} />
                                <Typography variant="body2" color="text.primary">
                                  <strong>Important Note:</strong> {article.notes}
                                </Typography>
                              </Paper>
                            )}
                          </AccordionDetails>
                        </Accordion>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}

            {filteredArticles.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No knowledge base guides match your search "{searchQuery}".
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Admin Modal to Add / Edit Knowledge Base Guide */}
        {isAdmin && (
          <Dialog
            open={editorOpen}
            onClose={() => setEditorOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <Box component="form" onSubmit={handleSaveArticle}>
              <DialogTitle sx={{ fontWeight: 700 }}>
                {editingId ? 'Edit Knowledge Base Guide' : 'Add New Knowledge Base Guide'}
              </DialogTitle>
              <DialogContent dividers>
                <FormControl fullWidth size="small" margin="dense">
                  <InputLabel>Section / Category</InputLabel>
                  <Select
                    label="Section / Category"
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value as KBCategory)}
                  >
                    {(kbSections.some((section) => section.key === formCategory)
                      ? kbSections
                      : [
                          ...kbSections,
                          { key: formCategory, title: formCategory, subtitle: '', color: '#1565C0' },
                        ]
                    ).map((section) => (
                      <MenuItem key={section.key} value={section.key}>
                        {section.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Topic Title"
                  margin="dense"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                />

                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Short Summary"
                  margin="dense"
                  value={formSummary}
                  onChange={(e) => setFormSummary(e.target.value)}
                />

                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={4}
                  size="small"
                  label="Step-by-Step Instructions (one step per line)"
                  margin="dense"
                  value={formStepsText}
                  onChange={(e) => setFormStepsText(e.target.value)}
                  helperText="Enter each step on a new line"
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Important Note / Tip (optional)"
                  margin="dense"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={savingArticle}>
                  {savingArticle ? 'Saving...' : 'Save Guide'}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
        )}

        {isAdmin && (
          <Dialog
            open={Boolean(sectionEditorKey)}
            onClose={() => setSectionEditorKey(null)}
            maxWidth="sm"
            fullWidth
          >
            <Box component="form" onSubmit={handleSaveSection}>
              <DialogTitle sx={{ fontWeight: 700 }}>Edit Section</DialogTitle>
              <DialogContent dividers>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Section heading"
                  margin="dense"
                  value={sectionTitle}
                  onChange={(e) => setSectionTitle(e.target.value)}
                />
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Section / Category"
                  margin="dense"
                  value={sectionCategory}
                  onChange={(e) => setSectionCategory(e.target.value)}
                  helperText="Guides in this section use this category name."
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  margin="dense"
                  multiline
                  minRows={2}
                  value={sectionSubtitle}
                  onChange={(e) => setSectionSubtitle(e.target.value)}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setSectionEditorKey(null)}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={savingSection}>
                  {savingSection ? 'Saving...' : 'Save Section'}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
        )}
      </Container>
    </Box>
  );
}
